/**
 * Countries, as a person reads and picks them.
 *
 * ONE picker serves the whole portal (owner, 2026-10-15): Mexico, the United States and Canada
 * first, then every other country alphabetically by its NAME in the reader's language. A person can
 * search by name or by ISO code, but the list shows names, never codes. What is STORED is the ISO
 * 3166-1 alpha-2 code, as it always was.
 *
 * Names come from the browser (`Intl.DisplayNames`), so no dictionary of country names is kept in the
 * app and the Spanish ones are the browser's own. The server shows no country to a person (the
 * connection country is internal data), so it keeps no table of names.
 */

import { activeLanguage } from '../i18n/language';

/** The 249 officially assigned ISO 3166-1 alpha-2 codes: the only list of codes in the app. */
export const COUNTRY_CODES: readonly string[] = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT',
  'AU', 'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI',
  'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY',
  'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM',
  'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK',
  'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL',
  'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR',
  'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN',
  'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS',
  'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW',
  'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP',
  'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM',
  'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM',
  'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF',
  'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW',
  'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
];

/** The operating markets, always offered first and in this order. */
export const FIRST_COUNTRIES: readonly string[] = ['MX', 'US', 'CA'];

export interface CountryOption {
  code: string;
  name: string;
}

export interface CountryChoice {
  /** Restrict the list to these codes (a field limited to the operating markets, or to a
   *  workflow catalog's own items). Omitted: every country. */
  codes?: readonly string[];
  /** The label to show for a code that is not a country, such as a catalog's "Other". */
  labels?: Readonly<Record<string, string>>;
}

const ISO_CODES = new Set(COUNTRY_CODES);
const namers = new Map<string, Intl.DisplayNames>();

/** The locale country names are written in for the reader's language. */
export function countryLocale(language: string): string {
  return language === 'es' ? 'es-MX' : 'en';
}

/** The name of an ISO code in `locale`, or null when it is not a country. Never the code. */
export function countryName(code: string, locale: string): string | null {
  if (!ISO_CODES.has(code)) return null;
  let namer = namers.get(locale);
  if (!namer) {
    namer = new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' });
    namers.set(locale, namer);
  }
  try {
    return namer.of(code) ?? null;
  } catch {
    return null;
  }
}

/** The name of a stored code as the person at this screen reads it; empty for a non-country. */
export function readerCountryName(code: string): string {
  return countryName(code, countryLocale(activeLanguage())) ?? '';
}

function byName(locale: string): (a: CountryOption, b: CountryOption) => number {
  return (a, b) => a.name.localeCompare(b.name, locale);
}

/**
 * The options of a country picker, in the order a person reads them: the operating markets, the
 * rest by name in `locale`, then anything that is not a country (a catalog's "Other").
 */
export function countryOptions(locale: string, choice: CountryChoice = {}): CountryOption[] {
  const wanted = [...new Set((choice.codes ?? COUNTRY_CODES).map((code) => code.toUpperCase()))];
  const countries: CountryOption[] = [];
  const others: CountryOption[] = [];
  for (const code of wanted) {
    const name = countryName(code, locale);
    if (name !== null) countries.push({ code, name });
    else if (choice.labels?.[code]) others.push({ code, name: choice.labels[code] });
  }
  const first = FIRST_COUNTRIES.flatMap((code) => countries.filter((c) => c.code === code));
  const rest = countries.filter((c) => !FIRST_COUNTRIES.includes(c.code)).sort(byName(locale));
  return [...first, ...rest, ...others];
}

/** Lower-case and without accents, so "mexico" finds "México". */
function folded(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

/** The options a search keeps: a code typed in full first, then names that contain the text. */
export function searchCountries(options: readonly CountryOption[], query: string): CountryOption[] {
  const wanted = folded(query);
  if (wanted === '') return [...options];
  const byCode = options.filter((o) => o.code.toLowerCase() === wanted);
  const byText = options.filter(
    (o) => o.code.toLowerCase() !== wanted && folded(o.name).includes(wanted),
  );
  return [...byCode, ...byText];
}
